/**
 * Listas e itens. Transcrição de `Docs/02-Dominio/ShoppingList.md`.
 *
 * ## O vínculo com produto é OPCIONAL, e isso é o desenho
 *
 * *"Uma lista que exige cadastrar produto antes de anotar 'ração' não vai ser
 * usada."* É também a última linha da definição de pronto da fase — *"item sem
 * vínculo funciona perfeitamente e não é tratado como incompleto"* —, e por isso
 * `productId` é `string | null` e **nada** neste módulo trata o `null` como
 * pendência: não há `isComplete`, não há aviso, não há badge.
 *
 * ## `text` nunca é vazio, e é `textFor` quem garante
 *
 * *"Se o usuário escolheu um produto sem digitar nada, `text` recebe o nome do
 * produto — assim a lista sempre é legível sem resolver referências."* Uma lista
 * que precisasse de uma segunda leitura para saber o que está escrito nela seria
 * lenta exatamente onde a fase exige velocidade.
 */

export type ListKind = 'shopping' | 'reminders'

export type ShoppingList = {
  id: string
  householdId: string
  name: string
  kind: ListKind
  icon: string
  /** Desnormalizados, mantidos por Cloud Function. O cliente não escreve. */
  itemCount: number
  pendingCount: number
  archivedAt: string | null
  createdAt: string
  updatedAt: string
}

export type ListItem = {
  id: string
  householdId: string
  listId: string

  /** Sempre presente. Ver `textFor`. */
  text: string
  /** Opcional, e o `null` é um estado completo — não uma pendência. */
  productId: string | null
  /**
   * O **grupo** (`ProductFamily`) a que o item se refere, quando ele foi
   * adicionado por sugestão de grupo em vez de embalagem.
   *
   * Numa lista de compras esta é quase sempre a granularidade certa: ninguém
   * decide entre a de 2 L e a lata na hora de escrever "coca" — decide na
   * prateleira, que é exatamente o que a comparação de `/grupos/$id` responde.
   *
   * Ver `vinculoDe` para a regra de quando os dois campos estão preenchidos.
   */
  familyId: string | null
  quantity: number | null
  unit: string | null
  note: string | null

  completed: boolean
  /** Instante ISO 8601. */
  completedAt: string | null
  completedBy: string | null

  /**
   * Instante ISO 8601 do lembrete. `null` é o normal: a maior parte dos itens
   * é "comprar ração", sem hora.
   */
  dueAt: string | null
  position: number

  /**
   * Quando a lista foi limpa. **O item não é apagado** — *"ver o que já foi
   * comprado é parte do valor"* —, ele sai da lista viva e continua no banco.
   */
  clearedAt: string | null

  createdBy: string
  createdAt: string
  updatedAt: string
}

/**
 * O texto de um item novo, com a invariante aplicada.
 *
 * Devolve `null` quando não há o que escrever — nem digitado, nem produto. Um
 * item sem texto seria uma linha em branco que ninguém consegue interpretar
 * depois, e apagar é mais trabalho do que não criar.
 */
export function textFor(typed: string, productName: string | null): string | null {
  const limpo = typed.trim()
  if (limpo !== '') return limpo
  const doProduto = productName?.trim() ?? ''
  return doProduto === '' ? null : doProduto
}

/**
 * A que o item se refere: uma embalagem, um grupo, ou nada.
 *
 * ## Quem escreve preenche UM, e a leitura mesmo assim decide
 *
 * O caminho de criação nunca põe os dois: a sugestão que a pessoa toca é de
 * grupo **ou** de produto. Ainda assim esta função escolhe em vez de reclamar,
 * e a escolha não é arbitrária — **a embalagem vence**.
 *
 * O motivo é o fluxo que isto abre: adicionar "coca" pelo grupo e, na
 * prateleira, fixar a garrafa que você de fato pôs no carrinho. Aí os dois
 * campos ficam preenchidos e significam *"era este grupo, virou esta
 * embalagem"* — e a embalagem é a informação mais específica das duas. Tratar
 * isso como dado inválido transformaria um estado útil em erro.
 */
export type VinculoDeItem =
  | { tipo: 'produto'; productId: string }
  | { tipo: 'grupo'; familyId: string }
  | null

export function vinculoDe(item: Pick<ListItem, 'productId' | 'familyId'>): VinculoDeItem {
  if (item.productId !== null) return { tipo: 'produto', productId: item.productId }
  if (item.familyId !== null) return { tipo: 'grupo', familyId: item.familyId }
  return null
}

/**
 * A ordem da lista na tela: pendentes primeiro, na ordem manual; concluídos
 * agrupados no fim.
 *
 * **Concluído mais recente primeiro**, e não na posição em que estava: quem
 * acabou de marcar por engano procura o item no topo do grupo, não no meio de
 * trinta linhas riscadas. Item sem `completedAt` (dado antigo, ou relógio do
 * servidor ainda não resolvido) cai no fim do grupo em vez de sumir.
 */
export function sortItems(items: ListItem[]): ListItem[] {
  const vivos = items.filter((i) => i.clearedAt === null)
  const pendentes = vivos
    .filter((i) => !i.completed)
    .sort((a, b) => a.position - b.position || a.createdAt.localeCompare(b.createdAt))
  const concluidos = vivos
    .filter((i) => i.completed)
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
  return [...pendentes, ...concluidos]
}

/**
 * O passo entre duas posições. **Mil**, e não um: o espaço é o que permite
 * inserir entre dois itens sem reescrever a lista inteira.
 */
export const PASSO = 1000

/** A posição de um item novo: depois de todos os pendentes. */
export function nextPosition(items: ListItem[]): number {
  const vivos = items.filter((i) => i.clearedAt === null)
  if (vivos.length === 0) return PASSO
  return Math.max(...vivos.map((i) => i.position)) + PASSO
}

/**
 * A posição para colocar um item **entre** outros dois — o resultado de arrastar
 * (ou de mover um degrau).
 *
 * `before`/`after` são as posições dos vizinhos; `null` quando é a ponta.
 *
 * **Devolve `null` quando não cabe mais nada no meio**, e quem chama renumera. É
 * a parte que uma implementação com `(a + b) / 2` em ponto flutuante erra em
 * silêncio: depois de ~50 inserções no mesmo lugar, a média de dois vizinhos é
 * igual a um deles, e dois itens passam a ter a mesma posição — a lista começa a
 * trocar de ordem sozinha a cada releitura, e nada acusa. Com inteiros, o
 * "não cabe" é detectável, e é isto que o detecta.
 */
export function positionBetween(before: number | null, after: number | null): number | null {
  if (before === null && after === null) return PASSO
  if (before === null) return (after ?? 0) - PASSO
  if (after === null) return before + PASSO
  if (after - before < 2) return null
  return Math.floor((before + after) / 2)
}

/** A renumeração: posições de `PASSO` em `PASSO`, na ordem dada. */
export function renumber(items: ListItem[]): { id: string; position: number }[] {
  return items.map((item, indice) => ({ id: item.id, position: (indice + 1) * PASSO }))
}

/**
 * Mover um item um degrau, entre os **pendentes**.
 *
 * Devolve o que gravar — `null` quando não há para onde ir (já é a ponta), e
 * `{ renumber: true }` quando o espaço acabou.
 *
 * **Só entre pendentes**, e a razão é a ordem da tela: um item que "desce" para
 * dentro do grupo de concluídos não teria para onde ir visualmente, porque
 * `sortItems` põe todo concluído no fim de qualquer jeito.
 */
export type Move =
  | { kind: 'position'; itemId: string; position: number }
  | { kind: 'renumber'; positions: { id: string; position: number }[] }

/**
 * Mover para uma posição qualquer da lista de pendentes — é o que o arrastar e
 * o menu ("topo", "fundo", "posição N") usam.
 *
 * ## Os vizinhos saem da lista JÁ REORDENADA, e não da original
 *
 * A versão anterior calculava os vizinhos do destino com aritmética que mudava
 * conforme a direção: subindo era `[destino - 1, destino]`, descendo era
 * `[destino, destino + 1]`. Estava certo, e era a parte que dava trabalho de
 * conferir toda vez.
 *
 * Aqui a lista é reordenada primeiro; depois os vizinhos são simplesmente quem
 * ficou ao lado. Uma direção só, nenhum caso especial — e mover três posições
 * de uma vez usa exatamente o mesmo caminho que mover uma.
 *
 * `null` quando não há o que fazer: item que não existe, ou destino que é o
 * lugar onde ele já está.
 */
export function moveTo(items: ListItem[], itemId: string, destino: number): Move | null {
  const pendentes = sortItems(items).filter((i) => !i.completed)
  const indice = pendentes.findIndex((i) => i.id === itemId)
  if (indice === -1) return null

  // Fora da lista vira a ponta mais próxima: "mover para a posição 99" numa
  // lista de 5 é um pedido claro, e recusá-lo seria pedantismo.
  const alvo = Math.min(Math.max(destino, 0), pendentes.length - 1)
  if (alvo === indice) return null

  const reordenado = [...pendentes]
  const [movido] = reordenado.splice(indice, 1)
  if (movido === undefined) return null
  reordenado.splice(alvo, 0, movido)

  const posicao = positionBetween(
    reordenado[alvo - 1]?.position ?? null,
    reordenado[alvo + 1]?.position ?? null,
  )
  if (posicao !== null) return { kind: 'position', itemId, position: posicao }

  // Não coube entre os vizinhos. Renumera a lista **já com o item no lugar
  // novo** — uma passada resolve, em vez de renumerar e mover de novo.
  return { kind: 'renumber', positions: renumber(reordenado) }
}

/** Um passo para cima ou para baixo. Açúcar sobre `moveTo`. */
export function moveOneStep(items: ListItem[], itemId: string, direction: -1 | 1): Move | null {
  const pendentes = sortItems(items).filter((i) => !i.completed)
  const indice = pendentes.findIndex((i) => i.id === itemId)
  if (indice === -1) return null
  const destino = indice + direction
  if (destino < 0 || destino >= pendentes.length) return null
  return moveTo(items, itemId, destino)
}

/**
 * Os lembretes que já venceram e ainda não foram avisados, dado um instante.
 *
 * **Item concluído não lembra.** Quem já comprou a ração não quer o push às
 * 18:00 dizendo para comprar ração. É a checagem que uma consulta por `dueAt`
 * sozinha não faz, e o custo de errar é o aviso que ensina a desligar tudo.
 */
export function dueReminders(items: ListItem[], now: string): ListItem[] {
  return items.filter(
    (i) =>
      i.dueAt !== null && !i.completed && i.clearedAt === null && i.dueAt <= now,
  )
}

/**
 * O texto do preço ao lado de um item vinculado: *"Último R$ 4,29 · Menor
 * R$ 3,49"*.
 *
 * **Devolve `null` quando não há dado**, e não "R$ 0,00" nem "—": *"o app é
 * honesto sobre não saber"* (definição de pronto da Fase 10). Um zero ali seria
 * lido como "de graça".
 */
export function priceHint(
  last: number | null,
  min: number | null,
  format: (cents: number) => string,
): string | null {
  const partes: string[] = []
  if (last !== null) partes.push(`Último ${format(last)}`)
  if (min !== null && min !== last) partes.push(`Menor ${format(min)}`)
  return partes.length === 0 ? null : partes.join(' · ')
}
